import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';

import { Button } from '~shared/shadcn/ui/button';

import {
  getImportResolutionPlaybook,
  reasonLabel,
  type ImportActionRowContext,
  type ImportResolutionAction,
} from './import-resolution-playbook';

type ActionLink =
  | {
      to: '/students/$studentId/details';
      params: { studentId: string };
    }
  | { to: '/family'; search: { parentId: string } }
  | { to: '/register-student'; search: { parentId: string } };

function resolveActionLink(
  action: ImportResolutionAction,
  row: ImportActionRowContext,
): ActionLink | null {
  switch (action.type) {
    case 'link_view_student':
      if (!row.createdStudentId) return null;
      return {
        to: '/students/$studentId/details',
        params: { studentId: row.createdStudentId },
      };
    case 'link_view_existing_student':
      if (!row.existingStudentId) return null;
      return {
        to: '/students/$studentId/details',
        params: { studentId: row.existingStudentId },
      };
    case 'link_view_family':
      if (!row.parentId) return null;
      return { to: '/family', search: { parentId: row.parentId } };
    case 'link_add_student':
      if (!row.parentId) return null;
      return { to: '/register-student', search: { parentId: row.parentId } };
    default:
      return null;
  }
}

interface BulkImportActionRowProps {
  row: ImportActionRowContext;
}

export function BulkImportActionRow({ row }: BulkImportActionRowProps) {
  const [manualOpen, setManualOpen] = useState(false);
  const playbook = getImportResolutionPlaybook(row.manualResolutionKey);

  const actionButtons = playbook.actions
    .map((action) => ({ action, link: resolveActionLink(action, row) }))
    .filter(
      (
        item,
      ): item is {
        action: ImportResolutionAction;
        link: NonNullable<ReturnType<typeof resolveActionLink>>;
      } => item.link != null && item.action.type !== 'export_row',
    );

  return (
    <div className='rounded-md border p-3 text-sm'>
      <div className='font-medium'>
        {row.studentFirstName} {row.studentLastName}
        {row.rowHint ? (
          <span className='ml-1 text-xs font-normal text-muted-foreground'>
            ({row.rowHint})
          </span>
        ) : null}
      </div>
      <div className='text-xs text-muted-foreground'>
        {row.parentFirstName ?? ''} {row.parentLastName ?? ''} · {row.parentEmail}
      </div>
      <p className='mt-2 text-muted-foreground'>
        {row.success
          ? (row.message ?? reasonLabel(row.reason))
          : reasonLabel(row.failureReason ?? row.reason)}
      </p>
      {actionButtons.length > 0 ? (
        <div className='mt-3 flex flex-wrap gap-2'>
          {actionButtons.map(({ action, link }) => (
            <Button key={action.type} variant='outline' size='sm' asChild>
              {link.to === '/students/$studentId/details' ? (
                <Link to={link.to} params={link.params}>
                  {action.label}
                </Link>
              ) : (
                <Link to={link.to} search={link.search}>
                  {action.label}
                </Link>
              )}
            </Button>
          ))}
        </div>
      ) : null}
      <button
        type='button'
        className='mt-3 flex items-center gap-1 text-xs font-medium text-primary'
        onClick={() => setManualOpen((v) => !v)}
      >
        {manualOpen ? (
          <ChevronDown className='size-3' />
        ) : (
          <ChevronRight className='size-3' />
        )}
        How to fix manually
      </button>
      {manualOpen ? (
        <ol className='mt-2 list-inside list-decimal space-y-1 text-xs text-muted-foreground'>
          {playbook.manualSteps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}
